import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import ApiService from '@dogfy/services/apiService'
import endpoints from '@dogfy/services/apiEndpoints'
import { getDogAgeInfo } from '@dogfy/lib/utils'
import { useSignUpStore } from '../../apps/website/src/stores/signUp'
import { useAccountStore } from '../../apps/cust/src/stores/account'
import config from '@/config'

import dayjs from 'dayjs'

export const useDogAdditionStore = defineStore('dogAddition', () => {
  // State
  // ----------------------
  const dogs = ref([])
  const dogsSimilarities = ref({
    areSameGender: true,
    areSameAge: true,
    areSameWeight: true,
    haveSameActivityLevel: true,
    haveSameDiseases: true,
    haveSameAppetiteLevel: true,
  })
  const hasReachedMaxDogs = ref(false)
  const quotation = ref(null)
  const breedStats = ref(null)
  const topBreeds = ref([])
  const currentStepIdx = ref(0)

  // Getters
  // ----------------------
  const areDogsEqual = computed(() => dogs.value.length > 1 && dogs.value.every((dog) => dog.breedCode === dogs.value[0].breedCode) && Object.values(dogsSimilarities.value).every(item => item))
  const dogNames = computed(() => dogs.value.map((dog) => dog.name || ''))
  const dogsNumber = computed(() => dogs.value.length)
  const dogAllowedMenus = computed(() => (index) => dogs.value[index].allowedMenus)
  const dogsGender = computed(() => dogs.value.some((dog) => dog.gender === 'male') ? 'male' : 'female')

  // Actions
  // ----------------------
  function setAreDogsEqual (areEqual = false) {
    for (const key in dogsSimilarities.value) {
      dogsSimilarities.value[key] = areEqual
    }
  }

  // Weight should be reset due to weight casuistries on the front. But should not be reset when data comes from the API.
  function setDogAge ({ dogIdx, birthdate, weight, adultIdealWeight }, options) {
    const { mustResetWeight = true } = options || {}
    const diffMonths = dayjs().diff(birthdate, 'month')
    const breedCode = dogs.value[dogIdx].breedCode
    const wasPuppyBefore = isPuppy(dogIdx)

    const ageInfo = getDogAgeInfo({ breedCode, age: diffMonths, weight, adultIdealWeight })

    dogs.value[dogIdx].birthdate = birthdate
    dogs.value[dogIdx].ageInfo = ageInfo

    if (!isPuppy(dogIdx) && !dogs.value[dogIdx].weight) {
      dogs.value[dogIdx].weight = ageInfo.idealWeight
    } else if (mustResetWeight && !wasPuppyBefore && isPuppy(dogIdx)) {
      dogs.value[dogIdx].weight = null
      delete dogs.value[dogIdx].shape
    }

    if (!isPuppy(dogIdx)) {
      delete dogs.value[dogIdx].adultEstimatedSize
    }
  }

  function setDogAllergies (dogIdx, allergyCodes) {
    dogs.value[dogIdx].allergyCodes = allergyCodes || []
    setDogAllowedMenus(dogIdx)
  }

  function getDogAllowedMenus (dogIdx) {
    const allowedMenus = dogs.value[dogIdx].allergyCodes?.map(code => config.allowedAllergiesMenus[code] || config.menus)
    const areAllMenuAllergiesSelected = dogs.value[dogIdx].allergyCodes?.filter(code => config.menus.includes(code)).length === config.menus.length

    if (areAllMenuAllergiesSelected) {
      // If all menu allergies are selected (chicken, turkey, beef, salmon), we can't recommend any menu.
      return []
    } else if (dogs.value[dogIdx]?.allergyCodes?.length > 1) {
      // If more than one allergy is selected, we need to find the common allowed menus for selected allergies.
      return allowedMenus.reduce((commonElements, array) => commonElements.filter(element => array.includes(element)), allowedMenus[0])
    } else {
      return allowedMenus?.length > 0 ? allowedMenus[0] : config.menus
    }
  }

  function setDogAllowedMenus (dogIdx) {
    const allowedMenus = getDogAllowedMenus(dogIdx)

    dogs.value[dogIdx].allowedMenus = allowedMenus
    setDogMenu(dogIdx)
  }

  function setDogMenu (dogIdx) {
    // In case the user has a trial menu set we must assign it as the dog menu.
    const dogTrialMenu = Object.keys(dogs.value[dogIdx]?.trial?.menu || {})
    dogs.value[dogIdx].dogMenu = dogTrialMenu.length > 0 ? dogTrialMenu : dogs.value[dogIdx].allowedMenus
  }

  function setDogDisease (dogIdx, diseaseCode) {
    if (dogs.value[dogIdx].diseaseCode === diseaseCode) return

    dogs.value[dogIdx].diseaseCode = diseaseCode
    dogs.value[dogIdx].allergyCodes = []

    const diseaseOption = diseaseCode ? config.dogDiseasesOptions.find(disease => disease.diseaseCode === diseaseCode) : null
    dogs.value[dogIdx].hasLimitingDisease = diseaseOption?.isLimiting ?? false

    // Now all menus are allowed.
    dogs.value[dogIdx].allowedMenus = config.menus
    dogs.value[dogIdx].dogMenu = []
  }

  // Maps dogs data to API dog data model.
  function getMappedDogData () {
    return dogs.value.map((dog) => {
      // We ensure to always send at least 1-m if the dog is puppy.
      const diffMonths = Math.max(dayjs().diff(dog.birthdate, 'month'), 1)

      const { isPuppy, isSenior } = getDogAgeInfo({ breedCode: dog.breedCode, age: diffMonths, weight: dog.weight, adultIdealWeight: dog.ageInfo?.idealWeight })

      let ageGroup = 'adult'
      if (isSenior) ageGroup = 'senior'
      else if (isPuppy) ageGroup = `puppy-${diffMonths}m`

      // Data of the dog that will be sent to the API.
      return {
        name: dog.name,
        gender: dog.gender,
        breedCode: dog.breedCode,
        birthdate: dog.birthdate,
        weight: dog.weight,
        adultEstimatedSize: dog.adultEstimatedSize,
        activityLevel: dog.activityLevel,
        ageGroup,
        allergyCodes: dog.allergyCodes,
        appetiteLevel: dog.appetiteLevel,
        shape: dog.shape || 'normal',
        currentFeeding: dog.currentFeeding,
        diseaseCode: dog.diseaseCode,
        isPregnantOrNursing: dog.isPregnantOrNursing,
        isSterilized: dog.isSterilized,
        menuTypes: dog.dogMenu,
      }
    })
  }

  async function createDogsQuotation () {
    const mappedDogs = getMappedDogData()
    quotation.value = null

    try {
      const { data } = await ApiService.post(endpoints.self.quotation, { dogs: mappedDogs })
      const { dogs: adjustedDogs, totalOrderBags, subscriptionAmount, subscriptionDiscount } = data
      setDogsData(adjustedDogs)

      quotation.value = {
        totalOrderBags,
        subscriptionAmount,
        subscriptionDiscount,
      }
    } catch (err) {
      console.error(' !! Could not create dogs quotation.', err)
      throw err
    }
  }

  async function addDogsToCustomer () {
    try {
      // This endpoint can add only a dog per call. This reuses the same endpoint that Backoffice has.
      getMappedDogData()

      for (const dog of dogs.value) {
        dog.menuTypes = Object.keys(dog.order.menu)
        await ApiService.post(endpoints.self.dogs._, dog)
      }
    } catch (err) {
      console.error(' !! Could not add dogs to customer.', err)
      throw err
    }

    await useAccountStore().fetchAccountData()
  }

  async function fetchBreedStats () {
    try {
      const { data } = await ApiService.get(endpoints.config.breedStats)
      breedStats.value = data
    } catch (err) {
      console.error('  !! Could not fetch breed stats', err)
    }
  }

  async function fetchTopBreeds (country) {
    try {
      const { data } = await ApiService.get(endpoints.config.breedStats, {
        top: 3,
        country,
      })
      topBreeds.value = Object.values(data).map((breed) =>
        Number(breed._id),
      )
    } catch (err) {
      console.error('  !! Could not fetch top breeds', err)
    }
  }

  function getDogBreedCount (breedCode) {
    return Object.keys(breedStats.value || {}).reduce((total, country) => {
      const breedStat =
          breedStats.value?.[country]?.find((b) => b._id === breedCode.toString())
            ?.count || 0
      return total + breedStat
    }, 0)
  }

  function isCrossBreed (dogIdx) {
    return dogs.value.length > dogIdx ? [1, 2].includes(dogs.value[dogIdx].breedCode) : false
  }

  function isPuppy (dogIdx) {
    return dogs.value[dogIdx].ageInfo?.isPuppy ?? (isCrossBreed(dogIdx) && dayjs().diff(dogs.value[dogIdx].birthdate, 'month') < 21)
  }

  function hasRequiredDogData () {
    const requiredKeys = ['name', 'breedCode', 'gender', 'birthdate', 'weight', 'dogMenu']

    return dogs.value.every(dog => {
      const keys = dog.ageInfo?.isAdult ? [...requiredKeys, 'activityLevel'] : requiredKeys

      return keys.every(key => !!dog[key])
    })
  }

  function setDogsData (newDogs) {
    // It's important that every time the dogs are updated, this function runs to adjust the fields.
    dogs.value = newDogs

    for (let dogIdx = 0; dogIdx < dogs.value.length; dogIdx++) {
      let adultIdealWeight

      if (isCrossBreed(dogIdx) && isPuppy(dogIdx)) {
        const { weightRange = [] } = config.dogEstimatedSizes.find(el => el.size === dogs.value[dogIdx].adultEstimatedSize) || {}
        adultIdealWeight = Math.ceil(weightRange.reduce((a, b) => a + b, 0) / 2)
      }

      // Disallow reseting the weight as data comes from the API.
      setDogAge({ dogIdx, birthdate: dogs.value[dogIdx].birthdate, weight: dogs.value[dogIdx].weight, adultIdealWeight }, { mustResetWeight: false })
      setDogAllowedMenus(dogIdx)
    }
  }

  function $reset () {
    dogs.value = []
    dogsSimilarities.value = {
      areSameGender: true,
      areSameAge: true,
      areSameWeight: true,
      haveSameActivityLevel: true,
      haveSameDiseases: true,
      haveSameAppetiteLevel: true,
    }
    currentStepIdx.value = 0
    hasReachedMaxDogs.value = false
    quotation.value = null
  }

  return {
    dogs,
    quotation,
    dogsSimilarities,
    hasReachedMaxDogs,
    areDogsEqual,
    dogNames,
    dogsNumber,
    dogAllowedMenus,
    dogsGender,
    breedStats,
    topBreeds,
    currentStepIdx,
    isCrossBreed,
    isPuppy,
    setAreDogsEqual,
    setDogAge,
    setDogAllergies,
    setDogsData,
    getDogAllowedMenus,
    setDogAllowedMenus,
    setDogMenu,
    setDogDisease,
    getMappedDogData,
    createDogsQuotation,
    addDogsToCustomer,
    fetchBreedStats,
    fetchTopBreeds,
    getDogBreedCount,
    hasRequiredDogData,
    $reset,
  }
}, {
  customPersist: {
    storage: 'localStorage',
    paths: [
      'dogs',
      'hasReachedMaxDogs',
      'dogsSimilarities',
      'currentStepIdx',
    ],
    // Store should be overwritten by persisted data when there's no lead created
    // or when lead data is persisted as this means that there's a payment process.
    shouldOverwriteStore: () => {
      const signUpStore = useSignUpStore()
      return !signUpStore.leadId || signUpStore.$isPersisted()
    },
  },
})
